import type { AudienceBand } from "@shared/plan-matrix";
import type { SupplierShellTheme } from "@shared/schemes/supplierTheme";
import type {
  PortalAppearance,
  PortalSettings,
  RentalState,
} from "@shared/consumer-tvod";

/**
 * The purchase portal's view of the `/api/interactiveplayer/v1/portal/*` wire.
 *
 * The server's own types live in `src/server` (route-local), which a browser
 * bundle may not import, so these restate the shapes the SPA reads. They are
 * written against `api/portalContext.ts`, `api/portalAccount.ts` and
 * `api/portalCommerce.ts` as built; a field the SPA does not read is left out
 * rather than guessed.
 */

export type Lang = "sv" | "en";

export interface PortalContext {
  portal: { host: string; name: string };
  skin: {
    brand: Record<string, unknown>;
    identity: {
      displayName?: string;
      website?: string;
      copyright?: string;
      [k: string]: unknown;
    };
    theme: SupplierShellTheme;
  };
  /** Hero, type pairing, corners, density, favicon. Absent = defaults. */
  appearance?: PortalAppearance;
  settings: PortalSettings & { defaultLocale: Lang };
  catalogue: {
    providers: Array<{ id: string; name: string }>;
    packages: Array<{ id: string; name: string }>;
  };
  /** Sources the footer must credit; `["tmdb"]` once TMDB data is stored. */
  attributions?: Array<"tmdb">;
}

export interface Profile {
  id: string;
  email: string;
  displayName: string;
  locale: Lang | null;
  marketingOptIn: boolean;
  emailVerifiedAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  /** ORG-5/6: an organisation's purchaser, one of its viewers, or a person. */
  kind?: "individual" | "purchaser" | "viewer";
}

/** One higher plan the purchaser may move to, and what it costs now. */
export interface UpgradeOption {
  band: AudienceBand;
  levelId: string;
  label: string;
  films: number | null;
  yearlyPrice: number;
  chargeNow: number;
}

/** A play account the purchaser shared the organisation's films with. */
export interface Viewer {
  id: string;
  email: string;
  name: string;
  status: "invited" | "active" | "revoked";
  invitedAt: string;
}

/** GET /org - the purchaser's buy flow (ORG-5). */
export interface OrgPlanView {
  account: { id: string; name: string };
  plan: {
    id: string;
    band: AudienceBand;
    levelId: string;
    label: string;
    films: number | null;
    yearlyPrice: number;
    vatBps: number;
    startsAt: string;
    renewsAt: string;
  };
  picks: Array<{ assetId: string; pickedAt: string }>;
  picksLeft: number | null;
  upgrades: UpgradeOption[];
}

export interface TitleCard {
  assetId: string;
  type: string;
  title: string;
  synopsis: string;
  images: { poster?: string; banner?: string; landscape?: string };
}

export interface Rental {
  state: RentalState;
  startedAt: string | null;
  endsAt: string | null;
  startBy: string | null;
  playWindowSeconds: number | null;
  firstPlayStartsClock: boolean;
}

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  next: number | null;
}

export interface Genre {
  id: string;
  label: string;
}

export interface GenreCount extends Genre {
  count: number;
}

export type TitleSort = "popular" | "az" | "year" | "length";
export const TITLE_SORTS: readonly TitleSort[] = [
  "popular",
  "az",
  "year",
  "length",
];

export interface CatalogueTitle extends TitleCard {
  locked: boolean;
  rental: Rental | null;
  genres?: Genre[];
  year?: number | null;
  durationSeconds?: number | null;
}

export interface TitlesPage {
  signedIn: boolean;
  titles: CatalogueTitle[];
  pagination: Pagination;
  truncated?: boolean;
  /** A provider's titles could not be listed just now: the page is short. */
  unavailable?: boolean;
  serverTime: string;
  sort?: TitleSort | "relevance";
  q?: string | null;
  /** With `?genre=`: the genre, or null when this catalogue has no such genre. */
  genre?: Genre | null;
}

/** One carousel on the store page; its items come from GET /titles. */
export interface StoreRail {
  id: string;
  kind: "all" | "genre";
  label: string;
  genreId?: string;
  count: number;
  query: { sort: TitleSort; genre?: string };
}

/**
 * The rich title page (slice J, `services/portalTitleDetail.ts`). Every
 * optional field is ABSENT when the catalogue has nothing true for it - the
 * page shows a field only when it is here.
 */
export interface TitleRichDetail {
  assetId: string;
  type: string;
  title: string;
  originalTitle?: string;
  year?: number;
  durationSeconds?: number;
  genres?: Genre[];
  ageRating?: { label: string; value: number; country: "SE" };
  directors?: string[];
  cast?: string[];
  synopsis?: string;
  audioLanguages?: string[];
  subtitleLanguages?: string[];
  series?: { seasons: number; episodes: number };
  images: { backdrop?: string; poster?: string };
  score?: {
    value: number;
    source: "imdb" | "tmdb" | "provider" | "manual";
    votes: number | null;
  };
}

export interface TitleDetail {
  title: TitleCard;
  /** Absent from an older API: the page falls back to `title`. */
  detail?: TitleRichDetail;
  signedIn: boolean;
  /** Signed in only: is it on Min lista. */
  onMyList?: boolean;
  locked: boolean;
  rental: Rental | null;
  serverTime: string;
}

/** One title on Min lista. */
export interface ListItem extends TitleCard {
  savedAt: string;
}

export interface MyTitle extends TitleCard {
  rental: Rental | null;
}

export interface MyTitles {
  items: MyTitle[];
  serverTime: string;
}

export interface Device {
  id: string;
  kind: "web" | "app";
  deviceLabel: string | null;
  createdAt: string;
  lastUsedAt: string;
  current: boolean;
}
